<template>
<div class="image-uploader">
    <div class="upload-box" @dragover.prevent @drop.prevent="handleDrop" :class="{ 'drag-over': isDragging }" @dragenter.prevent="isDragging = true" @dragleave.prevent="isDragging = false">
        <input type="file" ref="fileInput" @change="handleFileChange" accept="image/*" class="file-input">
        <div class="upload-content">
            <div v-if="loading" class="loading">Processing...</div>
            <div v-else-if="base64Image" class="preview">
                <img :src="'data:image/jpeg;base64,' + base64Image" alt="Preview">
                <p class="upload-info">Click or drag to upload a new image</p>
            </div>
            <div v-else class="upload-prompt">
                <i class="upload-icon"><img src="@/assets/images/upload-icon.svg"></i>
                <p>Drop image here or click to upload</p>
            </div>
        </div>
    </div>
    <div v-if="error" class="error-message">{{ error }}</div>
    <button class="form-button w-button balugabtn" :disabled="!base64Image || loading" @click="confirmImage">
        Confirm {{ toProperCase(imageTypeLabel) }} Upload
    </button>
</div>
</template>

  
  
<script>
export default {
    name: 'ImageUploader',
    props: {
        imageType: {
            type: String,
            required: true,
            validator: (value) => {
                return ['full_body_image', 'id_image', 'prescription_image','font_facing_image','top_head_image','id_front_image','id_back_image'].includes(value)
            }
        },
        questionId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            base64Image: '',
            error: '',
            loading: false,
            isDragging: false
        }
    },
    watch: {
        questionId() {
            // Reset the image when questionId changes
            this.base64Image = '';
        }
    },
    computed: {
        imageTypeLabel() {
            const labels = {
                profile: 'Profile Picture',
                id: 'ID Document',
                prescription: 'Prescription'
            }
            return labels[this.imageType] || this.imageType
        }
    },
    methods: {
        toProperCase(str) {
            return str.replace(/_/g, " ")
                .split(" ")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(" ");
        },
        async compressImage(file) {
            return new Promise((resolve) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onload = (event) => {
                    const img = new Image()
                    img.src = event.target.result

                    img.onload = () => {
                        const canvas = document.createElement('canvas')
                        let width = img.width
                        let height = img.height

                        // Scale down to max width of 1000px while maintaining aspect ratio
                        if (width > 1000) {
                            height = Math.round((height * 1000) / width)
                            width = 1000
                        }

                        canvas.width = width
                        canvas.height = height

                        const ctx = canvas.getContext('2d')
                        ctx.drawImage(img, 0, 0, width, height)

                        // Convert to JPEG and compress
                        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8)
                        // Remove MIME type prefix
                        const base64String = compressedBase64.split(',')[1]
                        resolve(base64String)
                    }
                }
            })
        },

        async processFile(file) {
            this.error = ''
            this.loading = true
            this.base64Image = '' // Reset the current image immediately

            try {
                if (!file) {
                    throw new Error('Please select a file')
                }

                if (!file.type.startsWith('image/')) {
                    throw new Error('Please upload an image file')
                }

                const base64String = await this.compressImage(file)
                this.base64Image = base64String
            } catch (err) {
                this.error = err.message
            } finally {
                this.loading = false
            }
        },

        handleFileChange(event) {
            const file = event.target.files[0]
            if (file) {
                this.processFile(file)
            }
        },

        handleDrop(event) {
            this.isDragging = false
            const file = event.dataTransfer.files[0]
            if (file) {
                this.processFile(file)
            }
        },

        confirmImage() {
            if (this.base64Image) {
                this.$emit('image-uploaded', {
                    type: this.imageType,
                    base64: this.base64Image
                })
            }
        }
    }
}
</script>
<style scoped>
.balugabtn {
   
    padding-top: 15px !important;
}

.image-uploader {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
}

.upload-label {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #333;
}

.upload-box {
    position: relative;
    border: 2px dashed #ccc;
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    margin-bottom: 15px;
}

.upload-box:hover {
    border-color: #666;
}

.drag-over {
    border-color: #2196F3;
    background-color: rgba(33, 150, 243, 0.1);
}

.file-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

.upload-content {
    padding: 20px;
}

.upload-icon {
    font-size: 48px;
    margin-bottom: 10px;
}

.upload-prompt p {
    margin: 10px 0;
    font-size: 16px;
    color: #666;
}

.upload-info {
    font-size: 14px;
    color: #999;
}

.preview {
    margin: 10px 0;
}

.preview img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 4px;
}

.loading {
    padding: 20px;
    color: #666;
}

.error-message {
    color: #f44336;
    margin-top: 10px;
    font-size: 14px;
}

.confirm-btn {
    width: 100%;
    padding: 12px;
    background-color: #2196F3;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
    margin-top: 10px;
    transition: background-color 0.3s ease;
}

.confirm-btn:hover:not(:disabled) {
    background-color: #1976D2;
}

.confirm-btn:disabled {
    background-color: #ccc;
    cursor: not-allowed;
}
</style>
